import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { UseAgentChatReturn } from "~/pages/appointments/hooks/useAgentChat";
import { prefersReducedMotion } from "~/utils/motion";

const BOTTOM_THRESHOLD_PX = 8;
const RESPONSE_ROOM_RATIO = 0.45;
const SCROLL_SMOOTH_TIME_MS = 140;
type TurnAnchor = { index: number; offset: number };

/** Preserve conversation context, making response room only when a send needs it. */
export function useMessageListScroll({
  messages,
  conversationId,
  isLoadingHistory,
}: Pick<
  UseAgentChatReturn,
  "messages" | "conversationId" | "isLoadingHistory"
>) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const messagesExtentRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const pinnedRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const scrollPositionsRef = useRef(new Map<string, number>());
  const turnStartsRef = useRef(new Map<string, TurnAnchor>());
  const activeTurnRef = useRef<TurnAnchor>();
  const animationRef = useRef<number>();
  const previousRef = useRef({
    conversationId,
    count: messages.length,
    isLoadingHistory,
  });
  const restoredConversationRef = useRef<string>();

  const updateTurnExtent = useCallback(() => {
    const container = messagesContainerRef.current;
    const content = messagesContentRef.current;
    const extent = messagesExtentRef.current;
    if (!container || !content || !extent) return;
    const row =
      activeTurnRef.current === undefined
        ? null
        : content.children.item(activeTurnRef.current.index);
    const inset =
      Number.parseFloat(getComputedStyle(container).scrollPaddingTop) || 0;
    const rowOffset = row
      ? row.getBoundingClientRect().top - content.getBoundingClientRect().top
      : 0;
    const offset = activeTurnRef.current?.offset ?? container.clientHeight;
    // Let layout reserve the whole turn's extent. A separately resized spacer
    // is too late when animated content shrinks: native scroll clamping happens
    // before ResizeObserver can replace the lost height, causing a small bounce.
    extent.style.minHeight = `${row ? Math.max(0, container.clientHeight + rowOffset - offset) : 0}px`;
    extent.style.paddingBottom = `${row ? inset : 0}px`;
  }, []);

  const cancelAnimation = useCallback(() => {
    if (animationRef.current !== undefined)
      cancelAnimationFrame(animationRef.current);
    animationRef.current = undefined;
  }, []);

  const followBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || !pinnedRef.current) return;
    if (prefersReducedMotion()) {
      cancelAnimation();
      container.scrollTop = container.scrollHeight;
      lastScrollTopRef.current = container.scrollTop;
      return;
    }
    if (animationRef.current !== undefined) return;
    let previousTime = performance.now();
    let position = container.scrollTop;
    let velocity = 0;
    const step = (time: number) => {
      animationRef.current = undefined;
      if (!pinnedRef.current) return;
      // Read the live destination: arriving tokens must not restart the easing.
      const target = Math.max(
        0,
        container.scrollHeight - container.clientHeight,
      );
      const elapsed = Math.max(0, time - previousTime);
      previousTime = time;
      // A critically damped spring keeps velocity continuous when a new line
      // moves the target. Keep subpixel position locally instead of feeding the
      // browser's rounded scrollTop back into the animation on every frame.
      const omega = 2 / SCROLL_SMOOTH_TIME_MS;
      const offset = position - target;
      const impulse = (velocity + omega * offset) * elapsed;
      const decay = Math.exp(-omega * elapsed);
      position = target + (offset + impulse) * decay;
      velocity = (velocity - omega * impulse) * decay;
      // Content can shrink as markdown settles; don't overshoot the new bottom.
      if (
        position > target ||
        (target - position < 0.25 && Math.abs(velocity) < 0.01)
      ) {
        position = target;
        velocity = 0;
      } else animationRef.current = requestAnimationFrame(step);
      container.scrollTop = position;
      lastScrollTopRef.current = container.scrollTop;
    };
    animationRef.current = requestAnimationFrame(step);
  }, [cancelAnimation]);

  const scrollToBottom = useCallback(() => {
    pinnedRef.current = true;
    setShowScrollToBottom(false);
    followBottom();
  }, [followBottom]);

  useLayoutEffect(() => {
    const previous = previousRef.current;
    previousRef.current = {
      conversationId,
      count: messages.length,
      isLoadingHistory,
    };
    if (previous.conversationId !== conversationId || isLoadingHistory)
      cancelAnimation();
    if (isLoadingHistory) {
      restoredConversationRef.current = undefined;
      activeTurnRef.current = undefined;
      updateTurnExtent();
      return;
    }
    const container = messagesContainerRef.current;
    if (!container) return;
    const lastMessage = messages.at(-1);
    // History rows have saved IDs. Only a new local send should reposition the
    // reader; opening history or receiving a remote reply must not look like Send.
    const sent =
      previous.conversationId === conversationId &&
      !previous.isLoadingHistory &&
      messages.length > previous.count &&
      lastMessage?.role === "user" &&
      !lastMessage.messageId;
    if (sent) {
      const content = messagesContentRef.current;
      const row = content?.children.item(messages.length - 1);
      if (content && row && container.clientHeight > 0) {
        const viewportTop =
          container.getBoundingClientRect().top + container.clientTop;
        const rowTop = row.getBoundingClientRect().top - viewportTop;
        const contentBottom =
          content.getBoundingClientRect().bottom - viewportTop;
        const needed = Math.max(
          0,
          contentBottom - container.clientHeight * (1 - RESPONSE_ROOM_RATIO),
        );
        // If there is already room, retain this exact reading position. Otherwise
        // move only far enough to leave response space, keeping prior text visible.
        turnStartsRef.current.set(conversationId, {
          index: messages.length - 1,
          offset: rowTop - needed,
        });
      }
    }
    activeTurnRef.current = turnStartsRef.current.get(conversationId);
    if (
      activeTurnRef.current !== undefined &&
      activeTurnRef.current.index >= messages.length
    ) {
      turnStartsRef.current.delete(conversationId);
      activeTurnRef.current = undefined;
    }
    updateTurnExtent();
    if (sent) {
      restoredConversationRef.current = conversationId;
      scrollToBottom();
    } else if (restoredConversationRef.current !== conversationId) {
      restoredConversationRef.current = conversationId;
      container.scrollTop =
        scrollPositionsRef.current.get(conversationId) ??
        container.scrollHeight;
      lastScrollTopRef.current = container.scrollTop;
      pinnedRef.current =
        container.scrollHeight - container.scrollTop - container.clientHeight <=
        BOTTOM_THRESHOLD_PX;
      setShowScrollToBottom(!pinnedRef.current);
    }
  }, [
    cancelAnimation,
    conversationId,
    isLoadingHistory,
    messages,
    scrollToBottom,
    updateTurnExtent,
  ]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (isLoadingHistory) return;
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      // Delayed programmatic events can arrive after more text. A gap alone
      // is not reader intent; only upward movement pauses following.
      if (distanceFromBottom <= BOTTOM_THRESHOLD_PX) pinnedRef.current = true;
      else if (container.scrollTop < lastScrollTopRef.current) {
        pinnedRef.current = false;
        cancelAnimation();
      }
      lastScrollTopRef.current = container.scrollTop;
      setShowScrollToBottom(!pinnedRef.current);
      scrollPositionsRef.current.set(conversationId, container.scrollTop);
    };
    const pause = () => {
      pinnedRef.current = false;
      cancelAnimation();
      setShowScrollToBottom(true);
    };
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) pause();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (["ArrowUp", "PageUp", "Home"].includes(event.key)) pause();
    };
    // Cancel before the next animation frame can overwrite a reader's gesture.
    container.addEventListener("wheel", onWheel, { passive: true });
    container.addEventListener("touchmove", pause, { passive: true });
    container.addEventListener("keydown", onKeyDown);
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      cancelAnimation();
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("touchmove", pause);
      container.removeEventListener("keydown", onKeyDown);
      container.removeEventListener("scroll", handleScroll);
    };
  }, [cancelAnimation, conversationId, isLoadingHistory]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = messagesContentRef.current;
    if (!container || !content) return;
    const observer = new ResizeObserver(() => {
      updateTurnExtent();
      followBottom();
    });
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, [followBottom, updateTurnExtent]);

  return {
    messagesContainerRef,
    messagesContentRef,
    messagesExtentRef,
    showScrollToBottom,
    scrollToBottom,
  };
}
